import { Box, Portal, Step, StepButton, StepConnector, Stepper, Theme } from '@mui/material';
import { Component, createRef } from 'react';
import { PaloTabs } from '@synergy/components-palo';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { tokens } from '@synergy/theme-palo';

import { ButtonWrapper } from './components/button-wrapper';
import { PaloDirtyStepScope, PaloStepDirtyDot } from '../palo/palo-dirty-context';
import { PoolStep } from '../../models/step';

// Styled per the PALO prototype's process steps (.steps): dense bar, numbered
// 20px circles (done = green, active = accent with a soft glow), thin
// connectors, no progress track. Literal tokens keep the bar theme-agnostic.
const styles = (theme: Theme) =>
    createStyles({
        wrapper: {
            display: 'block',
            position: 'relative',
            overflow: 'hidden',
        },
        progressBar: {
            position: 'relative',
            margin: 0,
            flex: 1,
            padding: '7px 16px 9px',

            '& > div': {
                zIndex: 1,
            },
            '& .MuiStepConnector-line': {
                borderColor: tokens.color.lineHard,
            },
        },
        backgroundProgress: {
            display: 'none',
        },
        componentStepButton: {
            margin: theme.spacing(-1, -2),
            padding: theme.spacing(1, 2),
            borderRadius: theme.spacing(3),

            '& a': {
                textDecoration: 'none',
                color: 'inherit',
            },
            '& .MuiStepIcon-root': {
                width: 20,
                height: 20,
                color: tokens.color.neutralSoft,
            },
            '& .MuiStepIcon-text': {
                fill: tokens.color.ink3,
                fontSize: '0.62rem',
                fontWeight: 650,
            },
            '& .MuiStepIcon-root.Mui-active': {
                color: tokens.color.accent,
                filter: `drop-shadow(0 0 0 ${tokens.color.accentSoft}) drop-shadow(0 0 2px ${tokens.color.accentSoft})`,
            },
            '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
                fill: tokens.color.inkInvert,
            },
            '& .MuiStepIcon-root.Mui-completed': {
                color: tokens.color.ok,
            },
            '& .MuiStepLabel-label': {
                fontSize: '12px',
                color: tokens.color.ink3,
            },
            '& .MuiStepLabel-label.Mui-active': {
                color: tokens.color.accentHi,
                fontWeight: 620,
            },
            '& .MuiStepLabel-label.Mui-completed': {
                color: tokens.color.ink2,
            },
        },
        stepButton: {
            borderRadius: theme.spacing(3 / 2),
            minHeight: theme.spacing(3),
            padding: theme.spacing(0, 2),
            boxShadow: 'none',
            textTransform: 'none',
        },
        step: {
            transition: 'opacity 0.2',
        },
        stepDisabled: {
            opacity: 0.5,
        },
    });

interface PoolStepperProps extends WithStyles<typeof styles> {
    className?: string;
    index: number;
    steps: PoolStep[];
    stepsDone?: number[];
    contentPortal?: Element | null;
    disableStepsByDefault?: boolean;
    // State can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialState?: any;
    connectorWidth?: string;
    unlocked?: boolean;
    /**
     * 'tabs' draws the steps as a tab bar instead of a numbered path: a
     * document is a form with tabs, not a sequence one is led through (#2207).
     * The engine underneath is the same, so validation and the content portal
     * are untouched. The legacy masks keep 'steps'.
     */
    variant?: 'steps' | 'tabs';
    /**
     * Whether a step has to be in order before the next one opens. True while a
     * document is being created, where the steps are a path; a document that is
     * already saved is looked at rather than filled in, and its steps open in
     * any order (#2132).
     */
    enforceStepOrder?: boolean;
    poolRef?: (ref: PoolStepperBase) => void;
    onIndexChange: (index: number) => void;
    // State can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onGlobalStateChange: (state: any) => void;
    // data can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCtaButtonChanged: (enabled: boolean, data: any) => void;
}

interface PoolStepperState {
    index: number;
    // data can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    globalState: any;
}
export class PoolStepperBase extends Component<PoolStepperProps, PoolStepperState> {
    public override state: PoolStepperState = {
        index: 0,
        globalState: {},
    };

    private stepperRef = createRef<HTMLDivElement>();

    private canActivateMap: { [index: number]: () => boolean } = {};
    private tryToActivateMap: { [index: number]: () => Promise<void> } = {};
    private disabledMap: { [index: number]: boolean } = {};
    private doneMap: { [index: number]: boolean } = {};
    private firstComponentWillMountTriggeredMap: { [index: number]: boolean } = {};

    // State can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private internalStateMap: { [index: number]: any } = {};

    private stepOffsets: { [key: number]: number } = {};

    constructor(props: PoolStepperProps) {
        super(props);

        this.isStepDisabled = this.isStepDisabled.bind(this);
    }

    /**
     * Keyed by the step that hands them in, not by the open one.
     *
     * A tab can hold more than one step (#2208) — head and positions are
     * written on one page — and with a single key the second of them would
     * overwrite what the first said about its own validity.
     */
    private setCanActivateOfStep = (stepIndex: number) => (canActivate: () => boolean) => {
        this.canActivateMap[stepIndex] = canActivate;
    };

    private setTryToActivateOfStep = (stepIndex: number) => (tryToActivate: () => Promise<void>) => {
        this.tryToActivateMap[stepIndex] = tryToActivate;
    };

    private setFirstComponentWillMountTriggeredOfStep = (stepIndex: number) => () => {
        this.firstComponentWillMountTriggeredMap[stepIndex] = true;
    };

    /** Every step shown under the open tab; on its own that is just the step. */
    private stepsOfActiveTab(): number[] {
        return this.stepsOfTab(this.state.index);
    }

    /** Every step shown under the tab of the given step. */
    private stepsOfTab(stepIndex: number): number[] {
        const tab = this.props.steps[stepIndex]?.tab;

        if (!tab) {
            return [stepIndex];
        }

        return this.props.steps.map((step, index) => (step.tab === tab ? index : -1)).filter(index => index >= 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private propagateInternalStateOfStep = (stepIndex: number) => (internalState: any) => {
        const nextGlobalState = { ...this.state.globalState, ...internalState };
        this.setState({ globalState: nextGlobalState });
        this.internalStateMap[stepIndex] = internalState;
        this.props.onGlobalStateChange(nextGlobalState);
    };

    private renderStep(stepIndex: number) {
        const Component = this.props.steps[stepIndex]?.component;

        if (!Component) {
            return null;
        }

        return (
            <PaloDirtyStepScope key={stepIndex} index={stepIndex}>
                <Component
                    internalState={this.internalStateMap[stepIndex]}
                    documentState={this.props.initialState}
                    firstComponentWillMountTriggered={!!this.firstComponentWillMountTriggeredMap[stepIndex]}
                    globalState={this.state.globalState}
                    setCanActivate={this.setCanActivateOfStep(stepIndex)}
                    setTryToActivate={this.setTryToActivateOfStep(stepIndex)}
                    setFirstComponentWillMountTriggered={this.setFirstComponentWillMountTriggeredOfStep(stepIndex)}
                    propagateInternalState={this.propagateInternalStateOfStep(stepIndex)}
                    unlocked={this.props.unlocked}
                    onCtaButtonChanged={this.props.onCtaButtonChanged}
                />
            </PaloDirtyStepScope>
        );
    }

    private renderContent(props: PoolStepperProps, state: PoolStepperState) {
        if (!props.steps[state.index]) {
            return null;
        }

        const content = <>{this.stepsOfActiveTab().map(stepIndex => this.renderStep(stepIndex))}</>;

        return props.contentPortal ? <Portal container={props.contentPortal}>{content}</Portal> : content;
    }

    private getTransformOffset(): number {
        return (this.stepOffsets[this.state.index] || 0) + 32;
    }

    private handleClick = (index: number) => () => {
        if (this.props.steps[index].component) {
            this.props.onIndexChange(index);
        } else {
            const callback = this.props.steps[index].callback;
            if (callback) {
                callback();
            }
        }
    };

    private isStepActive(index: number) {
        return this.state.index === index;
    }

    private isStepCompleted(index: number) {
        return this.doneMap[index] && !this.isStepActive(index);
    }

    private isStepDisabled(step: PoolStep, index: number) {
        // Prefer step property
        if (step.isActive !== undefined) {
            return !step.isActive;
        }

        // A saved document opens its tabs in any order. Skipping the check on
        // the way was not enough: the map below still greyed a tab depending on
        // which one was open last, e.g. offers seen from the documents tab
        // (#2265). What may be edited inside a tab is up to the status.
        if (false === this.props.enforceStepOrder) {
            return false;
        }

        // If no step property is active, check per disable map
        if (this.disabledMap[index] !== undefined) {
            return this.disabledMap[index];
        }

        return !!this.props.disableStepsByDefault || false;
    }

    private getStepClasses(disabled: boolean) {
        return this.props.classes.step + ' ' + (disabled ? this.props.classes.stepDisabled : '');
    }

    private updateProgressBackgroundPositions() {
        if (this.stepperRef.current) {
            const { children } = this.stepperRef.current;

            const stepperNodes = Array.from(children).filter((_, index) => !(index % 2)) as HTMLDivElement[];

            stepperNodes
                .map(stepperNode => stepperNode.offsetLeft)
                .forEach((value, index) => {
                    // Add 8 pixel from steps after 0, as they have an additional padding which makes
                    // the progress background render false
                    this.stepOffsets[index] = value + (index > 0 ? 8 : 0);
                });

            this.forceUpdate();
        }
    }

    /** Saving asks every step of the open tab, not only the one it started on. */
    public async canActivateAfterTriedToActivate(): Promise<boolean> {
        const indexes = this.stepsOfActiveTab();

        for (const index of indexes) {
            if ('function' === typeof this.tryToActivateMap[index]) {
                await this.tryToActivateMap[index]();
            }
        }

        return indexes.every(index => 'function' !== typeof this.canActivateMap[index] || this.canActivateMap[index]());
    }

    public override componentDidMount() {
        this.updateProgressBackgroundPositions();
        this.props.steps.forEach((_, index) => (this.disabledMap[index] = index > 1));
        this.props.poolRef?.(this);
    }

    private refreshDisabledState = (currentIndex: number) => {
        // Enable all done steps, last done + 1 step, current step and next step
        let stepAfterLastDone = 0;

        this.props.steps.forEach((_, index) => {
            this.disabledMap[index] = !this.doneMap[index];
            if (stepAfterLastDone === 0 && !this.doneMap[index]) {
                stepAfterLastDone = index;
            }
        });

        this.disabledMap[stepAfterLastDone] = false;
        this.disabledMap[currentIndex] = false;
        this.disabledMap[currentIndex + 1] = false;
    };

    public override async UNSAFE_componentWillReceiveProps(nextProps: PoolStepperProps) {
        const currentIndex = this.props.index;
        const nextIndex = nextProps.index;

        // Set doneMap from props if unmodified since start
        if (this.doneMap[0] === undefined && nextProps.stepsDone) {
            nextProps.stepsDone.forEach(stepIndex => {
                this.doneMap[stepIndex] = true;
            });
        }

        if (nextProps.initialState && nextProps.initialState !== this.props.initialState) {
            this.setState({ globalState: nextProps.initialState });
            this.props.steps.forEach((_, index) => (this.internalStateMap[index] = nextProps.initialState));
        }

        if (nextIndex === currentIndex) {
            this.refreshDisabledState(currentIndex);
            return;
        }

        if (this.tryToActivateMap[currentIndex] && typeof this.tryToActivateMap[currentIndex] === 'function') {
            await this.tryToActivateMap[currentIndex]();
        }

        // Only run step validations on index changes to latter steps, and not
        // when the step being opened only reads.
        if (nextIndex > currentIndex && !nextProps.steps[nextIndex]?.readOnly && false !== nextProps.enforceStepOrder) {
            if (this.canActivateMap[currentIndex] && typeof this.canActivateMap[currentIndex] === 'function') {
                const canActivate = this.canActivateMap[currentIndex]();

                if (!canActivate) {
                    // Set current and all following steps to undone
                    for (let index = currentIndex; index < this.props.steps.length; index++) {
                        this.doneMap[index] = false;
                    }
                    this.refreshDisabledState(currentIndex);
                    this.props.onIndexChange(this.state.index);
                    return;
                }
                this.doneMap[currentIndex] = true;
            }
        }

        this.refreshDisabledState(nextIndex);
        this.setState({ index: nextIndex });
        this.props.onIndexChange(nextIndex);
        this.updateProgressBackgroundPositions();
    }

    /**
     * The steps as a tab bar.
     *
     * A step that cannot be opened yet is left out rather than shown greyed:
     * on a document that does not exist yet, a tab for its follow-ups has
     * nothing to show, and ERPNext hides those too until the first save.
     */
    private renderTabs() {
        const { steps } = this.props;
        const active = this.stepsOfActiveTab();

        // One entry per tab. Steps that share a tab are one page, so they are
        // one heading, named after the first of them (#2208).
        const tabs = steps
            .map((step, index) => ({ step, index }))
            .filter(({ step, index }) => !step.tab || steps.findIndex(other => other.tab === step.tab) === index);

        return (
            <PaloTabs
                steps={tabs
                    .filter(
                        ({ step, index }) => !this.isStepDisabled(step, index) || false === this.props.enforceStepOrder
                    )
                    .map(({ step, index }) => {
                        const disabled = this.isStepDisabled(step, index);

                        return {
                            label: (
                                <>
                                    {step.name}
                                    <PaloStepDirtyDot stepIndex={this.stepsOfTab(index)} />
                                </>
                            ),
                            state: active.includes(index) ? 'now' : 'todo',
                            disabled,
                            onClick: disabled ? undefined : this.handleClick(index),
                        };
                    })}
            />
        );
    }

    public override render() {
        const { className, classes, steps, connectorWidth } = this.props;
        const { index } = this.state;

        if ('tabs' === this.props.variant) {
            return (
                <div className={className}>
                    {this.renderTabs()}
                    {this.renderContent(this.props, this.state)}
                </div>
            );
        }

        return (
            <div className={className}>
                <div className={classes.wrapper}>
                    <Box ref={this.stepperRef}>
                        <Stepper
                            id="stepper"
                            activeStep={index}
                            nonLinear={true}
                            className={classes.progressBar}
                            connector={
                                <StepConnector
                                    style={(connectorWidth && { width: connectorWidth, flex: '0 0 auto' }) || {}}
                                />
                            }
                        >
                            {steps.map((step, stepIndex) => {
                                const isStepDisabled = this.isStepDisabled(step, stepIndex);
                                return (
                                    <Step
                                        key={stepIndex}
                                        disabled={isStepDisabled}
                                        className={this.getStepClasses(isStepDisabled)}
                                        active={this.isStepActive(stepIndex)}
                                        completed={this.isStepCompleted(stepIndex)}
                                        color={(step.color && step.color) || 'primary'}
                                    >
                                        {step.component ? (
                                            <StepButton
                                                icon={step.icon || stepIndex + 1}
                                                className={classes.componentStepButton}
                                                onClick={this.handleClick(stepIndex)}
                                            >
                                                {step.name}
                                                <PaloStepDirtyDot stepIndex={stepIndex} />
                                            </StepButton>
                                        ) : (
                                            <ButtonWrapper
                                                variant="contained"
                                                size="small"
                                                color={(step.color && step.color) || 'primary'}
                                                className={classes.stepButton}
                                                disabled={isStepDisabled}
                                                onClick={this.handleClick(stepIndex)}
                                            >
                                                {step.name}
                                                <PaloStepDirtyDot stepIndex={stepIndex} />
                                            </ButtonWrapper>
                                        )}
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>
                    <div
                        className={classes.backgroundProgress}
                        style={{ transform: `translateX(calc( -100% + ${this.getTransformOffset()}px ))` }}
                    />
                </div>
                {this.renderContent(this.props, this.state)}
            </div>
        );
    }
}
export const PoolStepper = withStyles(styles)(PoolStepperBase);
